import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";
import type { KnowledgeDocument, Thread } from "@ncp/assistant-sdk";
import type { DefinitionDiff, DefinitionStatus, DefinitionVersion } from "../forms/api";
import type { ChangeSet } from "../releases/api";

// ---------------------------------------------------------------- workspaces

export type WorkspaceKind = "Department" | "Function";

/** `members` is the effective head count (explicit rows + holders of the member roles); `explicitMembers` are the rows on the Members tab. */
export interface WorkspaceUsage { members: number; explicitMembers: number; threads: number; threadsThisWeek: number; spendMonthUsd: number; pendingProposals: number; delegatedRunsMonth: number; artifacts: number }

export interface Workspace {
  id: string;
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  icon: string;
  color: string;
  kind: WorkspaceKind;
  parentKey?: string | null;
  assistantKeys: string[];
  defaultAssistantKey?: string | null;
  solutionKeys: string[];
  knowledgeCollectionKeys: string[];
  playbookKeys: string[];
  memberRoleKeys: string[];
  monthlyBudgetUsd: number;
  settings: Record<string, unknown> | null;
  enabled: boolean;
  sortOrder: number;
  sourcePackageKey?: string | null;
  createdAt: string;
  updatedAt: string;
  usage: WorkspaceUsage;
}

/** Editable fields of a workspace (what the form holds and what `PUT` sends). */
export interface WorkspaceBody {
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  icon?: string | null;
  color?: string | null;
  kind: WorkspaceKind;
  parentKey?: string | null;
  assistantKeys: string[];
  defaultAssistantKey?: string | null;
  solutionKeys: string[];
  knowledgeCollectionKeys: string[];
  playbookKeys: string[];
  memberRoleKeys: string[];
  monthlyBudgetUsd: number;
  settings: Record<string, unknown> | null;
  enabled: boolean;
  sortOrder: number;
}

export function workspaceBodyOf(w: Workspace): WorkspaceBody {
  return {
    name: w.name, nameAr: w.nameAr ?? null, description: w.description ?? null, descriptionAr: w.descriptionAr ?? null, icon: w.icon, color: w.color, kind: w.kind, parentKey: w.parentKey ?? null,
    assistantKeys: w.assistantKeys ?? [], defaultAssistantKey: w.defaultAssistantKey ?? null, solutionKeys: w.solutionKeys ?? [], knowledgeCollectionKeys: w.knowledgeCollectionKeys ?? [], playbookKeys: w.playbookKeys ?? [],
    memberRoleKeys: w.memberRoleKeys ?? [], monthlyBudgetUsd: w.monthlyBudgetUsd ?? 0, settings: w.settings ?? {}, enabled: w.enabled, sortOrder: w.sortOrder,
  };
}

export const EMPTY_WORKSPACE: WorkspaceBody = {
  name: "", nameAr: null, description: null, descriptionAr: null, icon: "Building2", color: "sand", kind: "Department", parentKey: null,
  assistantKeys: [], defaultAssistantKey: null, solutionKeys: ["assistant"], knowledgeCollectionKeys: [], playbookKeys: [], memberRoleKeys: [], monthlyBudgetUsd: 0, settings: {}, enabled: true, sortOrder: 100,
};

export interface WorkspaceMember {
  userId: string;
  displayName: string;
  displayNameAr?: string | null;
  email?: string | null;
  title?: string | null;
  isActive: boolean;
  roleKeys: string[];
  isOwner: boolean;
  source: string;
  addedByName?: string | null;
  addedAt: string;
  validUntil?: string | null;
}

export interface WorkspaceActivity {
  since: string;
  threadsByDay: Array<{ day: string; count: number }>;
  spendByAssistant: Array<{ key: string; costUsd: number; calls: number }>;
  topUsers: Array<{ userId: string; name: string; threads: number; messages: number }>;
  proposals: Array<{ status: string; count: number }>;
}

const WS = ["platform", "workspaces"] as const;

export function useWorkspaces() {
  const api = useApi();
  return useQuery({ queryKey: [...WS, "list"], queryFn: () => api.get<Workspace[]>("/api/platform/workspaces?includeDisabled=true"), staleTime: 10_000 });
}

export function useWorkspace(key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...WS, "one", key], queryFn: () => api.get<Workspace>(`/api/platform/workspaces/${encodeURIComponent(key!)}`), enabled: !!key, staleTime: 5_000 });
}

export function useWorkspaceMembers(key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...WS, "members", key], queryFn: () => api.get<WorkspaceMember[]>(`/api/platform/workspaces/${encodeURIComponent(key!)}/members`), enabled: !!key, staleTime: 5_000 });
}

export interface WorkspaceBuilderOptions { assistants: BuilderRef[]; solutions: BuilderRef[]; knowledgeCollections: BuilderRef[]; playbooks: BuilderRef[]; roles: BuilderRef[]; builtins: SpaceBuiltins }

/**
 * The AI Workspace's built-in widgets, navigation screens and matter tabs the space designer may place beside pages (`SpaceBuiltins` in
 * the API), and each playbook's own workbench tabs, shown where the manifest gives the playbook no tab groups.
 */
export interface SpaceBuiltins { home: string[]; nav: string[]; tabs: string[]; playbookTabs: Record<string, string[]> }

export function useWorkspaceOptions() {
  const api = useApi();
  return useQuery({ queryKey: [...WS, "options"], queryFn: () => api.get<WorkspaceBuilderOptions>("/api/platform/workspaces/options"), staleTime: 30_000 });
}

export function useWorkspaceActivity(key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...WS, "activity", key], queryFn: () => api.get<WorkspaceActivity>(`/api/platform/workspaces/${encodeURIComponent(key!)}/activity`), enabled: !!key, staleTime: 30_000 });
}

export function useWorkspaceActions() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: [...WS] });
    void qc.invalidateQueries({ queryKey: ["platform", "overview"] });
    void qc.invalidateQueries({ queryKey: ["assistant"] });
  };
  return {
    create: useSave({ mutationFn: (input: { key: string } & WorkspaceBody) => api.post<Workspace>("/api/platform/workspaces", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { key: string; body: WorkspaceBody }) => api.put<Workspace>(`/api/platform/workspaces/${encodeURIComponent(input.key)}`, input.body), onSuccess: invalidate }),
    remove: useSave({ mutationFn: (key: string) => api.delete<void>(`/api/platform/workspaces/${encodeURIComponent(key)}`), onSuccess: invalidate }),
    addMember: useSave({ mutationFn: (input: { key: string; userId: string; roleKeys?: string[]; isOwner?: boolean; validUntil?: string | null }) => api.put<WorkspaceMember>(`/api/platform/workspaces/${encodeURIComponent(input.key)}/members`, { userId: input.userId, roleKeys: input.roleKeys ?? [], isOwner: input.isOwner ?? false, validUntil: input.validUntil ?? null }), onSuccess: invalidate }),
    removeMember: useSave({ mutationFn: (input: { key: string; userId: string }) => api.delete<void>(`/api/platform/workspaces/${encodeURIComponent(input.key)}/members/${input.userId}`), onSuccess: invalidate }),
  };
}

// ---------------------------------------------------------------- assistants

/** Side-effect class of a tool (Ncp.Domain.Ai.ToolSideEffect): Personal applies a reversible change to the caller's own layout or draft at once. */
export type ToolSideEffect = "Read" | "Personal" | "Confirm" | "Reviewed";
export type ModelTier = "Fast" | "Reasoning" | "Vision";
export type GuardrailProfile = "Standard" | "Strict" | "Minimal";

export interface SuggestedPromptInput { text: string; textAr?: string | null; icon?: string | null }

export interface AssistantDefinition {
  id: string;
  key: string;
  version: number;
  status: DefinitionStatus;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  tags: string[];
  icon: string;
  color: string;
  systemPrompt: string;
  greeting?: string | null;
  greetingAr?: string | null;
  disclaimer?: string | null;
  disclaimerAr?: string | null;
  suggestedPrompts: SuggestedPromptInput[] | null;
  delegateAgentKeys: string[];
  toolKeys: string[];
  maxSideEffect: ToolSideEffect;
  knowledgeCollectionKeys: string[];
  connectorOperationKeys: string[];
  playbookKeys: string[];
  modelTier: ModelTier;
  modelId?: string | null;
  llmProviderKey?: string | null;
  guardrailProfile: GuardrailProfile;
  temperature: number;
  maxOutputTokens: number;
  historyWindow: number;
  turnTokenBudget: number;
  monthlyBudgetUsd: number;
  reviewerRoleKey?: string | null;
  allowAttachments: boolean;
  allowVoice: boolean;
  flagKey?: string | null;
  enabled: boolean;
  changeNote?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  publishedByName?: string | null;
  publishedByChangeSetId?: string | null;
  sourcePackageKey?: string | null;
  usedByWorkspaceKeys: string[];
}

/** The editable content of an assistant version – same shape as the canonical payload the server produces. */
export interface AssistantBody {
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  tags: string[];
  icon: string;
  color: string;
  systemPrompt: string;
  greeting?: string | null;
  greetingAr?: string | null;
  disclaimer?: string | null;
  disclaimerAr?: string | null;
  suggestedPrompts: SuggestedPromptInput[];
  delegateAgentKeys: string[];
  toolKeys: string[];
  maxSideEffect: ToolSideEffect;
  knowledgeCollectionKeys: string[];
  connectorOperationKeys: string[];
  playbookKeys: string[];
  modelTier: ModelTier;
  modelId?: string | null;
  llmProviderKey?: string | null;
  guardrailProfile: GuardrailProfile;
  temperature: number;
  maxOutputTokens: number;
  historyWindow: number;
  turnTokenBudget: number;
  monthlyBudgetUsd: number;
  reviewerRoleKey?: string | null;
  allowAttachments: boolean;
  allowVoice: boolean;
  flagKey?: string | null;
  enabled: boolean;
}

export function assistantBodyOf(d: AssistantDefinition): AssistantBody {
  return {
    name: d.name, nameAr: d.nameAr ?? null, description: d.description ?? null, descriptionAr: d.descriptionAr ?? null, tags: d.tags ?? [], icon: d.icon, color: d.color,
    systemPrompt: d.systemPrompt ?? "", greeting: d.greeting ?? null, greetingAr: d.greetingAr ?? null, disclaimer: d.disclaimer ?? null, disclaimerAr: d.disclaimerAr ?? null, suggestedPrompts: d.suggestedPrompts ?? [],
    delegateAgentKeys: d.delegateAgentKeys ?? [], toolKeys: d.toolKeys ?? [], maxSideEffect: d.maxSideEffect, knowledgeCollectionKeys: d.knowledgeCollectionKeys ?? [], connectorOperationKeys: d.connectorOperationKeys ?? [], playbookKeys: d.playbookKeys ?? [],
    modelTier: d.modelTier, modelId: d.modelId ?? null, llmProviderKey: d.llmProviderKey ?? null, guardrailProfile: d.guardrailProfile, temperature: d.temperature, maxOutputTokens: d.maxOutputTokens, historyWindow: d.historyWindow, turnTokenBudget: d.turnTokenBudget, monthlyBudgetUsd: d.monthlyBudgetUsd,
    reviewerRoleKey: d.reviewerRoleKey ?? null, allowAttachments: d.allowAttachments, allowVoice: d.allowVoice, flagKey: d.flagKey ?? null, enabled: d.enabled,
  };
}

export const EMPTY_ASSISTANT: AssistantBody = {
  name: "", nameAr: null, description: null, descriptionAr: null, tags: [], icon: "Sparkles", color: "sea", systemPrompt: "", greeting: null, greetingAr: null, disclaimer: null, disclaimerAr: null, suggestedPrompts: [],
  delegateAgentKeys: [], toolKeys: ["*"], maxSideEffect: "Reviewed", knowledgeCollectionKeys: [], connectorOperationKeys: [], playbookKeys: [],
  modelTier: "Fast", modelId: null, llmProviderKey: null, guardrailProfile: "Standard", temperature: 0.3, maxOutputTokens: 4096, historyWindow: 20, turnTokenBudget: 0, monthlyBudgetUsd: 0,
  reviewerRoleKey: null, allowAttachments: true, allowVoice: true, flagKey: null, enabled: true,
};

export interface AssistantRow {
  summary: {
    key: string; name: string; nameAr?: string | null; description?: string | null; tags: string[];
    publishedVersion?: number | null; publishedId?: string | null; publishedAt?: string | null;
    draftVersion?: number | null; draftId?: string | null; draftStatus?: DefinitionStatus | null;
    latestVersion: number; versionCount: number; updatedAt: string; updatedByName?: string | null; sourcePackageKey?: string | null;
  };
  icon: string;
  color: string;
  modelTier: ModelTier;
  maxSideEffect: ToolSideEffect;
  delegateCount: number;
  knowledgeCount: number;
  enabled: boolean;
  usedByWorkspaceKeys: string[];
  threadsMonth: number;
  spendMonthUsd: number;
}

export interface BuilderRef { key: string; name: string; nameAr?: string | null; hint?: string | null }
export interface BuilderAgent { key: string; name: string; nameAr?: string | null; kind: "Compiled" | "Configured"; description?: string | null }
export interface BuilderTool { key: string; description: string; sideEffect: ToolSideEffect; source: string }
export interface AssistantBuilderOptions {
  agents: BuilderAgent[];
  tools: BuilderTool[];
  knowledgeCollections: BuilderRef[];
  playbooks: BuilderRef[];
  roles: BuilderRef[];
  llmProviders: BuilderRef[];
  workspaces: BuilderRef[];
  modelTiers: ModelTier[];
  guardrailProfiles: GuardrailProfile[];
}

const AS = ["platform", "assistants"] as const;

export function useAssistants(params: { q?: string; tag?: string; status?: string } = {}) {
  const api = useApi();
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.tag) qs.set("tag", params.tag);
  if (params.status) qs.set("status", params.status);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({ queryKey: [...AS, "list", params], queryFn: () => api.get<AssistantRow[]>(`/api/platform/assistants${suffix}`), staleTime: 10_000 });
}

export function useAssistantDefinition(key: string | undefined, version?: number) {
  const api = useApi();
  return useQuery({
    queryKey: [...AS, "one", key, version ?? "latest"],
    queryFn: () => api.get<AssistantDefinition>(`/api/platform/assistants/${encodeURIComponent(key!)}${version ? `?version=${version}` : ""}`),
    enabled: !!key,
    staleTime: 5_000,
  });
}

export function useAssistantBuilderOptions() {
  const api = useApi();
  return useQuery({ queryKey: [...AS, "options"], queryFn: () => api.get<AssistantBuilderOptions>("/api/platform/assistants/options"), staleTime: 60_000 });
}

export function useAssistantActions() {
  const api = useApi();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: [...AS] });
    void qc.invalidateQueries({ queryKey: ["platform", "change-sets"] });
    void qc.invalidateQueries({ queryKey: ["platform", "definitions"] });
  };
  return {
    create: useSave({ mutationFn: (input: { key: string; body: AssistantBody; changeNote?: string }) => api.post<AssistantDefinition>("/api/platform/assistants", input), onSuccess: invalidate }),
    duplicate: useSave({ mutationFn: (input: { key: string; newKey: string; name?: string }) => api.post<AssistantDefinition>(`/api/platform/assistants/${encodeURIComponent(input.key)}/duplicate`, { newKey: input.newKey, name: input.name }), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { id: string; body: AssistantBody; changeNote?: string }) => api.put<AssistantDefinition>(`/api/platform/assistants/${input.id}`, { body: input.body, changeNote: input.changeNote }), onSuccess: invalidate }),
    newVersion: useSave({ mutationFn: (input: { key: string; changeNote?: string }) => api.post<AssistantDefinition>(`/api/platform/assistants/${encodeURIComponent(input.key)}/versions`, { changeNote: input.changeNote }), onSuccess: invalidate }),
    discard: useSave({ mutationFn: (id: string) => api.delete<void>(`/api/platform/assistants/${id}`), onSuccess: invalidate }),
    stage: useSave({ mutationFn: (input: { id: string; titleHint?: string }) => api.post<ChangeSet>(`/api/platform/assistants/${input.id}/stage`, { titleHint: input.titleHint }), onSuccess: invalidate }),
    withdraw: useSave({ mutationFn: (id: string) => api.post<AssistantDefinition>(`/api/platform/assistants/${id}/withdraw`), onSuccess: invalidate }),
    /** Opens a test-bench thread pinned to a definition version; chat then streams through the Surface API. */
    openTestThread: useSave({ mutationFn: (input: { definitionId: string; workspaceKey?: string | null; locale?: string }) => api.post<Thread>("/api/platform/assistants/test-bench/threads", input), failed: t("admin:common.error"), onSuccess: () => void qc.invalidateQueries({ queryKey: [...AS, "test-threads"] }) }),
  };
}

export function useTestThreads(key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...AS, "test-threads", key], queryFn: () => api.get<Thread[]>(`/api/platform/assistants/test-bench/threads?key=${encodeURIComponent(key!)}`), enabled: !!key, staleTime: 5_000 });
}

// ---------------------------------------------------------------- versions & diff for any definition resource

export type DefinitionResource = "forms" | "assistants" | "agents" | "automations" | "connectors" | "record-types" | "reports" | "screens" | "tools" | "rules" | "services";

export function useDefinitionVersions(resource: DefinitionResource, key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "definitions", resource, "versions", key], queryFn: () => api.get<DefinitionVersion[]>(`/api/platform/${resource}/${encodeURIComponent(key!)}/versions`), enabled: !!key, staleTime: 5_000 });
}

export function useDefinitionDiff(resource: DefinitionResource, key: string | undefined, from: number | undefined, to: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["platform", "definitions", resource, "diff", key, from, to],
    queryFn: () => api.get<DefinitionDiff>(`/api/platform/${resource}/${encodeURIComponent(key!)}/diff?from=${from}&to=${to}`),
    enabled: !!key && from !== undefined && to !== undefined && from !== to,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------- knowledge collections

export type KnowledgeVisibility = "Public" | "Internal" | "AgentsOnly";

export type KnowledgeSourceKind = "upload" | "url" | "crawl" | "snapshot" | "sharepoint" | "onedrive";
export const SYNCABLE_SOURCE_KINDS: KnowledgeSourceKind[] = ["sharepoint", "onedrive", "url", "crawl", "snapshot"];
/** Document files of the economic-sources snapshot (one per collection it feeds). */
export const SNAPSHOT_COLLECTIONS = ["economic-statistics", "economic-studies", "economic-policies", "economic-methods", "federal-legislation"] as const;

/** Where documents come from. Keys are stable once synced: documents remember the source that filed them. Schedules are cron in UAE time. */
export interface KnowledgeSource { key?: string; kind: KnowledgeSourceKind; name?: string | null; config?: Record<string, unknown>; schedule?: string | null; enabled?: boolean }

export type KnowledgeRunStatus = "Queued" | "Running" | "Succeeded" | "PartiallySucceeded" | "Failed";
export type KnowledgeRunAction = "added" | "updated" | "unchanged" | "removed" | "skipped" | "failed" | "reembedded";
export interface KnowledgeRunItem { item: string; title: string; action: KnowledgeRunAction; detail?: string | null; documentId?: string | null }

export interface KnowledgeRun {
  id: string;
  collectionKey: string;
  sourceKey?: string | null;
  sourceKind: string;
  trigger: "Manual" | "Schedule" | "Reembed";
  status: KnowledgeRunStatus;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  requestedByName?: string | null;
  itemsSeen: number;
  added: number;
  updated: number;
  unchanged: number;
  removed: number;
  failed: number;
  chunks: number;
  error?: string | null;
  items?: KnowledgeRunItem[] | null;
}

export const isRunActive = (r: Pick<KnowledgeRun, "status">) => r.status === "Queued" || r.status === "Running";

export interface KnowledgeSourcePreviewItem { externalId: string; title: string; url?: string | null; size?: number | null; modifiedAt?: string | null; supported: boolean; note?: string | null }
export interface KnowledgeSourcePreview { succeeded: boolean; error?: string | null; total: number; items: KnowledgeSourcePreviewItem[] }

export interface ConsoleKnowledgeCollection {
  id: string;
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  icon: string;
  workspaceKey?: string | null;
  visibility: KnowledgeVisibility;
  sources: KnowledgeSource[] | null;
  defaultDocumentKind: string;
  uploadRoleKeys: string[];
  documents: number;
  chunks: number;
  lastDocumentAt?: string | null;
  lastIngestedAt?: string | null;
  lastIngestionError?: string | null;
  enabled: boolean;
  isBuiltIn: boolean;
  sourcePackageKey?: string | null;
  usedByWorkspaceKeys: string[];
  usedByAssistantKeys: string[];
  updatedAt: string;
}

export interface KnowledgeCollectionBody {
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  icon?: string | null;
  workspaceKey?: string | null;
  visibility: KnowledgeVisibility;
  sources: KnowledgeSource[];
  defaultDocumentKind: string;
  uploadRoleKeys: string[];
  enabled: boolean;
}

export function collectionBodyOf(c: ConsoleKnowledgeCollection): KnowledgeCollectionBody {
  return {
    name: c.name, nameAr: c.nameAr ?? null, description: c.description ?? null, descriptionAr: c.descriptionAr ?? null, icon: c.icon, workspaceKey: c.workspaceKey ?? null, visibility: c.visibility,
    sources: c.sources ?? [], defaultDocumentKind: c.defaultDocumentKind, uploadRoleKeys: c.uploadRoleKeys ?? [], enabled: c.enabled,
  };
}

export const EMPTY_COLLECTION: KnowledgeCollectionBody = { name: "", nameAr: null, description: null, descriptionAr: null, icon: "Library", workspaceKey: null, visibility: "Internal", sources: [{ kind: "upload" }], defaultDocumentKind: "Guideline", uploadRoleKeys: [], enabled: true };

export const DOCUMENT_KINDS = ["Law", "CabinetDecision", "MinisterialDecision", "Agreement", "Guideline", "Policy", "Strategy", "Report", "Statistics", "Methodology", "Study", "Opinion", "Precedent", "Procedure", "Faq", "InternationalGuidance", "Briefing", "Paper", "Framework", "Minutes", "Feedback", "Presentation"] as const;

const KN = ["platform", "knowledge"] as const;

export function useKnowledgeCollections(workspace?: string) {
  const api = useApi();
  return useQuery({ queryKey: [...KN, "list", workspace ?? ""], queryFn: () => api.get<ConsoleKnowledgeCollection[]>(`/api/platform/knowledge/collections?includeDisabled=true${workspace ? `&workspace=${encodeURIComponent(workspace)}` : ""}`), staleTime: 10_000 });
}

export function useKnowledgeCollection(key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...KN, "one", key], queryFn: () => api.get<ConsoleKnowledgeCollection>(`/api/platform/knowledge/collections/${encodeURIComponent(key!)}`), enabled: !!key, staleTime: 5_000 });
}

export function useCollectionDocuments(key: string | undefined, params: { q?: string; kind?: string; includeInactive?: boolean } = {}) {
  const api = useApi();
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.kind) qs.set("kind", params.kind);
  if (params.includeInactive) qs.set("includeInactive", "true");
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({ queryKey: [...KN, "documents", key, params], queryFn: () => api.get<KnowledgeDocument[]>(`/api/platform/knowledge/collections/${encodeURIComponent(key!)}/documents${suffix}`), enabled: !!key, staleTime: 5_000 });
}

/** Recent synchronisation and re-embedding runs; polls while one is queued or running. */
export function useKnowledgeRuns(key: string | undefined, take = 20) {
  const api = useApi();
  return useQuery({
    queryKey: [...KN, "runs", key, take],
    queryFn: () => api.get<KnowledgeRun[]>(`/api/platform/knowledge/collections/${encodeURIComponent(key!)}/runs?take=${take}`),
    enabled: !!key,
    refetchInterval: (q) => (q.state.data?.some(isRunActive) ? 2_000 : false),
  });
}

export function useKnowledgeRun(key: string | undefined, id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...KN, "run", key, id],
    queryFn: () => api.get<KnowledgeRun>(`/api/platform/knowledge/collections/${encodeURIComponent(key!)}/runs/${id}`),
    enabled: !!key && !!id,
    refetchInterval: (q) => (q.state.data && isRunActive(q.state.data) ? 2_000 : false),
  });
}

export function useKnowledgeActions() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: [...KN] });
    void qc.invalidateQueries({ queryKey: ["assistant", "knowledge"] });
  };
  return {
    create: useSave({ mutationFn: (input: { key: string } & KnowledgeCollectionBody) => api.post<ConsoleKnowledgeCollection>("/api/platform/knowledge/collections", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { key: string; body: KnowledgeCollectionBody }) => api.put<ConsoleKnowledgeCollection>(`/api/platform/knowledge/collections/${encodeURIComponent(input.key)}`, input.body), onSuccess: invalidate }),
    remove: useSave({ mutationFn: (key: string) => api.delete<void>(`/api/platform/knowledge/collections/${encodeURIComponent(key)}`), onSuccess: invalidate }),
    refreshCounts: useSave({ mutationFn: (key: string) => api.post<void>(`/api/platform/knowledge/collections/${encodeURIComponent(key)}/refresh-counts`), onSuccess: invalidate }),
    /** Uploads go through the Surface API so the same intake (OCR, chunking, embeddings) is used everywhere. */
    upload: useSave({
      mutationFn: (input: { key: string; file: File; title?: string; titleAr?: string; kind?: string; issuer?: string; issuedOn?: string; sourceUrl?: string }) => {
        const form = new FormData();
        form.append("file", input.file);
        for (const [k, v] of Object.entries(input)) if (k !== "key" && k !== "file" && v) form.append(k, String(v));
        return api.upload<{ document: KnowledgeDocument; pageCount: number; usedVision: boolean }>(`/api/assistant/knowledge/collections/${encodeURIComponent(input.key)}/documents`, form);
      },
      onSuccess: invalidate,
    }),
    removeDocument: useSave({ mutationFn: (id: string) => api.delete<void>(`/api/assistant/knowledge/documents/${id}`), onSuccess: invalidate }),
    /** Queues a run of a saved source; the run reports progress through {@link useKnowledgeRuns}. */
    syncSource: useSave({ mutationFn: (input: { key: string; sourceKey: string }) => api.post<KnowledgeRun>(`/api/platform/knowledge/collections/${encodeURIComponent(input.key)}/sources/${encodeURIComponent(input.sourceKey)}/sync`), onSuccess: invalidate }),
    reembed: useSave({ mutationFn: (key: string) => api.post<KnowledgeRun>(`/api/platform/knowledge/collections/${encodeURIComponent(key)}/reembed`), onSuccess: invalidate }),
    /**
     * Lists what a source would bring in, without filing anything; works on unsaved drafts. Silent: a source that could not be read answers
     * `succeeded: false` (a SaveError "refused" carrying the preview), which the dialog shows inline; it reports any other failure itself.
     */
    testSource: useSave({ mutationFn: (input: { key: string; source: KnowledgeSource }) => api.post<KnowledgeSourcePreview>(`/api/platform/knowledge/collections/${encodeURIComponent(input.key)}/sources/test`, input.source), report: "silent" }),
  };
}
